import { ExclamationCircleOutlined } from "@ant-design/icons"
import { Company, CsvTemplate } from "@finway-group/shared/lib/models"
import { Button, PageHeader, Tabs, Tooltip } from "antd"
import CompanySettingsModal from "Components/modals/companySettings.modal"
import CsvTemplateModal from "Components/modals/csvTemplate.modal"
import CustomBankSubjectModal from "Components/modals/customBankSubject.modal"
import CustomDatevInformationModal from "Components/modals/customDatevInformation.modal"
import DebitCardLimitBufferModal from "Components/modals/debitCardLimitBuffer.modal"
import MileageDistanceBufferModal from "Components/modals/mileageDistanceBuffer.modal"
import SelectedCompanyPreview from "Components/selectedCompanyPreview"
import React from "react"
import { Edit as EditIcon } from "react-feather"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"
import { useHistory } from "react-router-dom"
import { useModal } from "Shared/context/modal.context"
import { useCompanies, useCompany } from "Shared/hooks/company.hooks"
import { useIsCustomCsvTemplatesEnabled, useIsTravelEnabled } from "Shared/hooks/featureFlags.hooks"
import { RootState } from "Shared/store/rootState.interface"
import { getIBANPrintFormat, roundNumber } from "Shared/utils/helpers"
import useStateIfMounted from "Shared/utils/hooks/useStateIfMounted"
import { getTooltipPopupContainer } from "Shared/utils/helpers"

enum CompanyPreviewTabEnum {
    CONFIGS = "CONFIGS",
    FEATURES = "FEATURES",
    BUSINESS_ADDRESS = "BUSINESS_ADDRESS",
    DELIVERY_ADDRESS = "DELIVERY_ADDRESS",
}

const CompanySettings = () => {
    const { t } = useTranslation()
    const { showModal } = useModal()
    const history = useHistory()
    const [activeTab, setActiveTab] = useStateIfMounted(CompanyPreviewTabEnum.CONFIGS)
    const [isShowingCustomBankSubjectModal, setIsShowingCustomBankSubjectModal] = useStateIfMounted(false)
    const [isShowingCustomDatevInfoModal, setIsShowingCustomDatevInfoModal] = useStateIfMounted(false)
    const [isShowingDebitCardLimitBufferModal, setIsShowingDebitCardLimitBufferModal] = useStateIfMounted(false)
    const { _id, name, logo, isRoot } = useCompany()
    const isCsvTemplatesEnabled = useIsCustomCsvTemplatesEnabled()
    const isTravelEnabled = useIsTravelEnabled()
    const company: any = useCompanies(false).find((data) => data._id === _id)
    const templates = useSelector(({ csvTemplates: { templates } }: RootState) => templates as CsvTemplate[])
    const handleOnCompanyEdit = (company: Company) => showModal(CompanySettingsModal, false, { company, isShowing: true, isEditMode: true })
    const handleOnTemplateEdit = (templates: CsvTemplate[]) => showModal(CsvTemplateModal, false, { templates, isShowing: true })
    const handleOnMileageDistanceBufferEdit = () => showModal(MileageDistanceBufferModal, false, { isShowing: true })

    return (
        <div id="finway-company-settings" className="w-full">
            <div className="md:hidden">
                <PageHeader
                    ghost={false}
                    onBack={() => history.push("/settings")}
                    title={<SelectedCompanyPreview name={name} logo={logo} isRoot={isRoot} />}
                    extra={[
                        <Button onClick={() => handleOnCompanyEdit(company)}>
                            <EditIcon />
                        </Button>,
                    ]}
                ></PageHeader>
            </div>

            <div className="justify-between items-center mb-10 hidden md:flex">
                <SelectedCompanyPreview name={name} logo={logo} isRoot={isRoot} />
                <div className="ml-10">
                    <Button onClick={() => handleOnCompanyEdit(company)}>
                        <EditIcon />
                        <span>{company ? t("action:edit") : t("action:create")}</span>
                    </Button>
                </div>
            </div>

            {!company ? (
                <div>{t("missing:company_data")}</div>
            ) : (
                <Tabs defaultActiveKey={CompanyPreviewTabEnum.CONFIGS} activeKey={activeTab} className="pb-10" onTabClick={(key) => setActiveTab(key as CompanyPreviewTabEnum)}>
                    <Tabs.TabPane tab={t("label:company_settings")} key={CompanyPreviewTabEnum.CONFIGS}>
                        <div>
                            <ul className="description-list">
                                <li>
                                    <span className="title w-32">{t("label:company_identifier")}</span>
                                    <span className="text text-left ml-10 sm:ml-0">{company.companyIdentifier ? company.companyIdentifier : `-`}</span>
                                </li>
                                <li>
                                    <span className="title w-32">{t("label:iban")}</span>
                                    <span className="text text-left ml-10 sm:ml-0">{company.iban ? getIBANPrintFormat(company?.iban) : `-`}</span>
                                </li>
                                <li>
                                    <span className="title">{t("label:bic")}</span>
                                    <span className="text text-left">{company.bic}</span>
                                </li>
                                <li>
                                    <span className="title w-240">{t("label:default_creditor_number")}</span>
                                    <span className="text text-left">{company.defaultCreditorNumber}</span>
                                </li>
                                <li>
                                    <span className="title w-240">{t("label:default_employee_creditor_number")}</span>
                                    <span className="text text-left">{company.defaultEmployeeCreditorNumber}</span>
                                </li>
                            </ul>
                        </div>
                    </Tabs.TabPane>

                    <Tabs.TabPane tab={t("label:features")} key={CompanyPreviewTabEnum.FEATURES}>
                        <div>
                            <ul className="description-list">
                                <li>
                                    <span className="title w-240">{t("label:card_feature")}</span>
                                    <span className="text text-left">{company.cardsEnabled ? t("label:enabled") : t("label:disabled")}</span>
                                </li>
                                <li>
                                    <span className="title w-240">{t("label:liquidity_management_feature")}</span>
                                    <span className="text text-left">{company.liquidityManagementEnabled ? t("label:enabled") : t("label:disabled")}</span>
                                </li>
                                <li>
                                    <span className="title w-240">{t("label:auto_matching_feature")}</span>
                                    <span className="text text-left">{company.autoMatchEnabled ? t("label:enabled") : t("label:disabled")}</span>
                                </li>
                                <li>
                                    <span className="title w-240">
                                        <span className="title w-240"> {t("label:invoice_auto_matching_feature")}</span>
                                        <Tooltip
                                            getPopupContainer={getTooltipPopupContainer}
                                            title={t("tooltips:invoice_auto_matching_feature")}
                                            placement="right"
                                            className="align-middle"
                                        >
                                            <ExclamationCircleOutlined className="ml-6" />
                                        </Tooltip>
                                    </span>
                                    <span className="text text-left">{company.autoInvoiceMatchEnabled ? t("label:enabled") : t("label:disabled")}</span>
                                </li>
                                <li>
                                    <span className="title w-240">{t("label:auto_bank_payment_matching")}</span>
                                    <span className="text text-left">{company.bankingPaymentAutoMatchEnabled ? t("label:enabled") : t("label:disabled")}</span>
                                </li>
                                <li>
                                    <span className="title w-240">{t("label:allow_cost_center_2_creation")}</span>
                                    <span className="text text-left">{company.allowCostCenter2Creation ? t("label:enabled") : t("label:disabled")}</span>
                                </li>
                                <li>
                                    <span className="title w-240">{t("label:allow_workflow_adjustments")}</span>
                                    <span className="text text-left">{company.allowWorkflowAdjustments ? t("label:enabled") : t("label:disabled")}</span>
                                </li>
                                <li>
                                    <span className="title w-240">{t("label:show_auto_approver_warning")}</span>
                                    <span className="text text-left">{company.showAutoApproverWarning ? t("label:enabled") : t("label:disabled")}</span>
                                </li>
                                <li>
                                    <span className="title w-180">{t("label:auto_logout.title")}</span>
                                    <span className="text text-left">{t(`label:auto_logout.${company.autoLogout?.toLowerCase()}`)}</span>
                                </li>
                                <li>
                                    <span className="title w-240">
                                        {t("label:tagging_feature_enable")}
                                        <Tooltip getPopupContainer={getTooltipPopupContainer} title={t("info:tagging_feature_enable")} placement="right" className="align-middle">
                                            <ExclamationCircleOutlined className="ml-6" />
                                        </Tooltip>
                                    </span>
                                    <span className="text text-left">{company.taggingEnabled ? t("label:enabled") : t("label:disabled")}</span>
                                </li>
                                <li>
                                    <span className="title w-240">
                                        {t("label:editing_tags_enable")}
                                        <Tooltip getPopupContainer={getTooltipPopupContainer} title={t("info:editing_tags_enable")} placement="right" className="align-middle">
                                            <ExclamationCircleOutlined className="ml-6" />
                                        </Tooltip>
                                    </span>
                                    <span className="text text-left">{company.editingTagsEnabled ? t("label:enabled") : t("label:disabled")}</span>
                                </li>
                                <li>
                                    <span className="title w-240">{t("label:is_lexware_export_enabled")}</span>
                                    <span className="text text-left">{company.isLexwareExportEnabled ? t("label:enabled") : t("label:disabled")}</span>
                                </li>
                                <li>
                                    <span className="title w-240">
                                        <span className="title w-240">{t("label:expense_payment_flow_information.is_already_approved_and_paid_enabled")}</span>
                                        <Tooltip
                                            getPopupContainer={getTooltipPopupContainer}
                                            title={t("info:expense_payment_flow_information.is_already_approved_and_paid_enabled")}
                                            placement="right"
                                            className="align-middle"
                                        >
                                            <ExclamationCircleOutlined className="ml-6" />
                                        </Tooltip>
                                    </span>
                                    <span className="text text-left">
                                        {company.expensePaymentFlowInformation.isAlreadyApprovedAndPaidEnabled ? t("label:enabled") : t("label:disabled")}
                                    </span>
                                </li>
                                <li>
                                    <span className="title w-240">
                                        <span className="title w-240">{t("label:expense_payment_flow_information.is_already_paid_enabled")}</span>
                                        <Tooltip
                                            getPopupContainer={getTooltipPopupContainer}
                                            title={t("info:expense_payment_flow_information.is_already_paid_enabled")}
                                            placement="right"
                                            className="align-middle"
                                        >
                                            <ExclamationCircleOutlined className="ml-6" />
                                        </Tooltip>
                                    </span>
                                    <span className="text text-left">{company.expensePaymentFlowInformation.isAlreadyPaidEnabled ? t("label:enabled") : t("label:disabled")}</span>
                                </li>
                                <li>
                                    <span className="title w-240">
                                        <span className="title w-240">{t("label:expense_payment_flow_information.is_report_only_enabled")}</span>
                                        <Tooltip
                                            getPopupContainer={getTooltipPopupContainer}
                                            title={t("info:expense_payment_flow_information.is_report_only_enabled")}
                                            placement="right"
                                            className="align-middle"
                                        >
                                            <ExclamationCircleOutlined className="ml-6" />
                                        </Tooltip>
                                    </span>
                                    <span className="text text-left">
                                        {company.expensePaymentFlowInformation.isReportingOnlyEnabled ? t("label:enabled") : t("label:disabled")}
                                    </span>
                                </li>
                                <li>
                                    <span className="title w-240">
                                        <span className="title w-240">{t("label:vendor_creation_restricted")}</span>
                                        <Tooltip
                                            getPopupContainer={getTooltipPopupContainer}
                                            title={t("info:vendor_creation_restricted")}
                                            placement="right"
                                            className="align-middle"
                                        >
                                            <ExclamationCircleOutlined className="ml-6" />
                                        </Tooltip>
                                    </span>
                                    <span className="text text-left">{company.isVendorCreationRestricted ? t("label:enabled") : t("label:disabled")}</span>
                                </li>
                                <li>
                                    <span className="title w-240">
                                        <span className="title w-240">{t("label:append_audit_trail_to_invoice")}</span>
                                        <Tooltip title={t("info:append_audit_trail_to_invoice")} placement="right" className="align-middle">
                                            <ExclamationCircleOutlined className="ml-6" />
                                        </Tooltip>
                                    </span>
                                    <span className="text text-left">{company.isAppendAuditTrailToInvoiceEnabled ? t("label:enabled") : t("label:disabled")}</span>
                                </li>
                                <li>
                                    <span className="title w-240">{t("label:archiveExpensesAfterXDays")}</span>
                                    <span className="text text-left">
                                        {company.archiveAfterXDays} {t("label:days")}
                                    </span>
                                </li>
                                <li>
                                    <span className="title w-240">{t("label:archiveTransactionsAfterXDays")}</span>
                                    <span className="text text-left">
                                        {company.transactionArchiveAfterXDays} {t("label:days")}
                                    </span>
                                </li>
                                <li>
                                    <span className="title w-240">
                                        <span className="title w-240"> {t("label:custom_bank_subject.title")}</span>
                                        <Tooltip getPopupContainer={getTooltipPopupContainer} title={t("tooltips:custom_bank_subject")} placement="right" className="align-middle">
                                            <ExclamationCircleOutlined className="ml-6" />
                                        </Tooltip>
                                    </span>
                                    <span className="flex items-center space-x-12 text">
                                        <span className="overflow-x-auto w-240">
                                            {company.customBankSubjectParts.map((subjectPart: any) => t(`label:custom_bank_subject.${subjectPart.part}`)).join(", ")}
                                        </span>
                                        <EditIcon className="cursor-pointer" size={14} onClick={() => setIsShowingCustomBankSubjectModal(true)} />
                                    </span>
                                    <CustomBankSubjectModal
                                        isShowing={isShowingCustomBankSubjectModal}
                                        onCancel={() => setIsShowingCustomBankSubjectModal(false)}
                                        bankSubjectParts={company.customBankSubjectParts}
                                    />
                                </li>
                                <li>
                                    <span className="title w-240">
                                        <span className="title w-240">{t("label:custom_datev_settings")}</span>
                                        <Tooltip
                                            getPopupContainer={getTooltipPopupContainer}
                                            title={t("tooltips:datev.information_customisation")}
                                            placement="right"
                                            className="align-middle"
                                        >
                                            <ExclamationCircleOutlined className="ml-6" />
                                        </Tooltip>
                                    </span>
                                    <span className="flex items-center space-x-12 text">
                                        <EditIcon className="cursor-pointer" size={14} onClick={() => setIsShowingCustomDatevInfoModal(true)} />
                                    </span>
                                    <CustomDatevInformationModal
                                        isShowing={isShowingCustomDatevInfoModal}
                                        onCancel={() => setIsShowingCustomDatevInfoModal(false)}
                                        datevInfo={company.customDatevInformation}
                                        datevBooking={company.datevBookingText}
                                    />
                                </li>
                                <li>
                                    <span className="title w-240">
                                        <span className="title w-240">{t("label:vendorRulesShouldOverwrite")}</span>
                                        <Tooltip
                                            getPopupContainer={getTooltipPopupContainer}
                                            title={t("tooltips:vendorRulesShouldOverwrite")}
                                            placement="right"
                                            className="align-middle"
                                        >
                                            <ExclamationCircleOutlined className="ml-6" />
                                        </Tooltip>
                                    </span>
                                    <span className="text text-left">{company.vendorRulesOverwriteEmployeeRulesEnabled ? t("label:enabled") : t("label:disabled")}</span>
                                </li>
                                <li>
                                    <span className="title w-240">
                                        {t("label:allow_all_to_update_absences")}
                                        <Tooltip
                                            getPopupContainer={getTooltipPopupContainer}
                                            title={t("info:allow_all_to_update_absences")}
                                            placement="right"
                                            className="align-middle"
                                        >
                                            <ExclamationCircleOutlined className="ml-6" />
                                        </Tooltip>
                                    </span>
                                    <span className="text text-left">{company.allowAllToUpdateAbsences ? t("label:enabled") : t("label:disabled")}</span>
                                </li>
                                <li>
                                    <span className="title w-240">
                                        {t("label:show_cost_centers_ids")}
                                        <Tooltip title={t("info:show_cost_centers_ids")} placement="right" className="align-middle">
                                            <ExclamationCircleOutlined className="ml-6" />
                                        </Tooltip>
                                    </span>
                                    <span className="text text-left">{company.showCostCentersIds ? t("label:enabled") : t("label:disabled")}</span>
                                </li>
                                <li>
                                    <span className="title w-240 flex items-center">
                                        {t("label:debit_card_limit_buffer")}
                                        <Tooltip getPopupContainer={getTooltipPopupContainer} title={t("tooltips:debit_card_limit_buffer")} className="ml-5">
                                            <ExclamationCircleOutlined />
                                        </Tooltip>
                                    </span>
                                    <span className="flex items-center space-x-12 text">
                                        <span className="overflow-x-auto w-240">{company.debitCardLimitBuffer} %</span>
                                        <EditIcon className="cursor-pointer" size={14} onClick={() => setIsShowingDebitCardLimitBufferModal(true)} />
                                    </span>
                                    <DebitCardLimitBufferModal isShowing={isShowingDebitCardLimitBufferModal} onCancel={() => setIsShowingDebitCardLimitBufferModal(false)} />
                                </li>
                                {isCsvTemplatesEnabled && (
                                    <li>
                                        <span className="title w-240 flex items-center">
                                            {t("label:csv_settings:custom_csv_template")}
                                            <Tooltip getPopupContainer={getTooltipPopupContainer} title={t("tooltips:csv_template_settings")} className="ml-5">
                                                <ExclamationCircleOutlined />
                                            </Tooltip>
                                        </span>
                                        {templates.length ? (
                                            <span className="flex space-x-12 items-center text">
                                                {templates.length <= 3 ? (
                                                    <>
                                                        <span className="overflow-x-auto w-240"> {templates.map(({ name }) => name).join(", ")}</span>
                                                    </>
                                                ) : (
                                                    <span className="text text-left"> {t("label:csv_settings:total_templates", { numberOfTemplates: templates.length })}</span>
                                                )}

                                                <EditIcon className="cursor-pointer" size={14} onClick={() => handleOnTemplateEdit(templates)} />
                                            </span>
                                        ) : (
                                            <Button onClick={() => handleOnTemplateEdit(templates)}>
                                                <span>{t("label:csv_settings:set_up")}</span>
                                            </Button>
                                        )}
                                    </li>
                                )}
                                {isTravelEnabled && (
                                    <li>
                                        <span className="title w-300">
                                            {t("label:mileage.distance_buffer")}
                                            <Tooltip
                                                getPopupContainer={getTooltipPopupContainer}
                                                title={t("tooltips:mileage.distance_buffer")}
                                                placement="right"
                                                className="align-middle"
                                            >
                                                <ExclamationCircleOutlined className="ml-6" />
                                            </Tooltip>
                                        </span>
                                        <span className="flex items-center space-x-12 text">
                                            <span className="overflow-x-auto w-240">
                                                {company.mileageDistanceBuffer ? (
                                                    <span className="flex justify-end items-center gap-12">
                                                        {roundNumber(company.mileageDistanceBuffer) + " km"}
                                                        <EditIcon className="cursor-pointer" size={14} onClick={handleOnMileageDistanceBufferEdit} />
                                                    </span>
                                                ) : (
                                                    <Button onClick={handleOnMileageDistanceBufferEdit}>{t("label:mileage.setup")}</Button>
                                                )}
                                            </span>
                                        </span>
                                    </li>
                                )}
                            </ul>
                        </div>
                    </Tabs.TabPane>

                    <Tabs.TabPane tab={t("label:business_address")} key={CompanyPreviewTabEnum.BUSINESS_ADDRESS}>
                        <div>
                            <ul className="description-list">
                                <li>
                                    <span className="title">{t("label:company_name")}</span>
                                    <span className="text text-left">{company.name || "-"}</span>
                                </li>
                                <li>
                                    <span className="title">{t("label:address_line")}</span>
                                    <span className="text text-left">{company.businessAddress?.addressLine2 || "-"}</span>
                                </li>
                                <li>
                                    <span className="title">{t("label:city")}</span>
                                    <span className="text text-left">{company.businessAddress?.city || "-"}</span>
                                </li>
                                <li>
                                    <span className="title">{t("label:country")}</span>
                                    <span className="text text-left">{company.businessAddress?.country || "-"}</span>
                                </li>
                                <li>
                                    <span className="title">{t("label:zip")}</span>
                                    <span className="text text-left">{company.businessAddress?.zip || "-"}</span>
                                </li>
                            </ul>
                        </div>
                    </Tabs.TabPane>
                    <Tabs.TabPane tab={t("label:delivery_address")} key={CompanyPreviewTabEnum.DELIVERY_ADDRESS}>
                        <div>
                            <ul className="description-list">
                                {company.deliverySameAsBusinessAddress ? (
                                    <li>
                                        <span>{t("label:same_as_business_address")}</span>
                                    </li>
                                ) : (
                                    <>
                                        <li>
                                            <span className="title">{t("label:company_name")}</span>
                                            <span className="text text-left">{company.name || "-"}</span>
                                        </li>
                                        <li>
                                            <span className="title">{t("label:address_line")}</span>
                                            <span className="text text-left">{company.deliveryAddress?.addressLine2 || "-"}</span>
                                        </li>
                                        <li>
                                            <span className="title">{t("label:city")}</span>
                                            <span className="text text-left">{company.deliveryAddress?.city || "-"}</span>
                                        </li>
                                        <li>
                                            <span className="title">{t("label:country")}</span>
                                            <span className="text text-left">{company.deliveryAddress?.country || "-"}</span>
                                        </li>
                                        <li>
                                            <span className="title">{t("label:zip")}</span>
                                            <span className="text text-left">{company.deliveryAddress?.zip || "-"}</span>
                                        </li>
                                    </>
                                )}
                            </ul>
                        </div>
                    </Tabs.TabPane>
                </Tabs>
            )}
        </div>
    )
}
export default React.memo(CompanySettings)
